import type { Core, PreviewPlayer } from "@/client/core/Core.api";
import type { LabsResource, LabsResourceID } from "@/client/core/platform-types/labs.api";
import { createSpliceSampleInputFile } from "@/client/utils/createSpliceSampleInputFile";
import { createSignal, type Listen, type RemoveListener } from "@/client/utils/signal";
import type { SpliceSearchResult } from "@/shared/splice-search.api";
import type { SamplePreviewCache } from "./sample-preview-cache";

export type SamplePreviewPlaybackState =
    | { readonly isPlaying: false }
    | { readonly isPlaying: true; readonly uuid: string; readonly isBuffering: boolean };

export interface SamplePreviewPlayer {
    readonly onState: Listen<SamplePreviewPlaybackState>;
    readonly getState: () => SamplePreviewPlaybackState;
    /** Fraction of the current preview played, 0 when nothing is playing. */
    readonly getProgress_0to1: () => number;
    /** Auditions `result`, replacing whatever was playing, from `startOffset_s` in. */
    readonly play: (result: SpliceSearchResult, startOffset_s?: number) => void;
    /**
     * Plays a resource that already exists: a file the user dropped in as a
     * seed, or an audition carried over from the previous page. Reported
     * under `playbackId`, or the resource's own id when none is given. Owned
     * resources go when the next audition starts; a user's file never does.
     */
    readonly playResource: (id: LabsResourceID<"wav">, options?: PlayResourceOptions) => void;
    readonly stop: () => void;
    /** Stops and removes the audition resource. For when the panel goes away. */
    readonly release: () => void;
    /**
     * What is playing right now, for the next page to pick up: the sample
     * and how far in it is. Undefined when nothing is playing. The next page
     * fetches the preview again rather than reusing the stored resource,
     * because website resources are namespaced per app and an audition
     * stored on one page cannot be seen from another.
     */
    readonly getResumeState: () => SamplePreviewResume | undefined;
}

export interface PlayResourceOptions {
    readonly startOffset_s?: number;
    readonly isOwned?: boolean;
    readonly playbackId?: string;
}

export interface SamplePreviewResume {
    readonly uuid: string;
    readonly position_s: number;
}

/**
 * Marks a resource as an audition: stored only so the renderer can play it,
 * and removed when the next one starts. Anything still carrying the tag at
 * boot is a leftover from a page that closed mid-audition, and is swept.
 */
export const kAuditionTag = "labs:audition";

/** How a stored resource is named in playback state, distinct from any catalog uuid. */
export const getResourcePlaybackId = (id: LabsResourceID<"wav">): string => `resource:${id.value}`;

const isAuditionResource = (resource: LabsResource): boolean => {
    const tags: unknown = (resource.metadata as { tags?: unknown }).tags;
    return Array.isArray(tags) && tags.includes(kAuditionTag);
};

/** Removes audition resources an earlier page left behind. */
export const sweepAuditionResources = async (core: Core): Promise<void> => {
    const all = await core.actions.requestAllResources();
    const stale = all.filter(isAuditionResource).map((resource) => resource.id);
    if (stale.length > 0) {
        await core.actions.deleteResources(stale);
    }
};

interface Audition {
    readonly uuid: string;
    readonly resourceId: LabsResourceID<"wav">;
    readonly player: PreviewPlayer;
    readonly removeListener: RemoveListener;
    /** Whether the resource was stored for this audition, and so goes when it does. */
    readonly isOwned: boolean;
}

/**
 * Auditioning goes through the same preview player as every other playback
 * in Labs, so it sits in the one graph the host renders and yields to and
 * from the app's own audio the way every other audition does.
 *
 * That player needs a stored resource, and a browse is not a library: thirty
 * auditions must not leave thirty files behind. So an audition is stored
 * tagged, kept only while it is the current one (a second press replays it
 * without another decode), and deleted when the next sample takes its place.
 */
export const createSamplePreviewPlayer = (
    core: Core,
    previews: SamplePreviewCache,
): SamplePreviewPlayer => {
    const stateSignal = createSignal<SamplePreviewPlaybackState>();
    let state: SamplePreviewPlaybackState = { isPlaying: false };
    let current: Audition | undefined;
    // Only the newest request speaks: fetch, decode and store are all
    // asynchronous, and the sample pressed second must not lose to the one
    // pressed first finishing later.
    let generation = 0;

    const setState = (next: SamplePreviewPlaybackState): void => {
        state = next;
        stateSignal.notify(next);
    };

    const setStopped = (): void => {
        if (state.isPlaying) {
            setState({ isPlaying: false });
        }
    };

    const discard = (audition: Audition): void => {
        audition.player.stop();
        audition.removeListener();
        if (audition.isOwned) {
            void core.actions.deleteResources([audition.resourceId]);
        }
    };

    const listenForEnd = (player: PreviewPlayer, uuid: string): RemoveListener =>
        player.signal.listen((playback) => {
            if (!playback.isPlaying && state.isPlaying && state.uuid === uuid) {
                setState({ isPlaying: false });
            }
        });

    const start = (audition: Audition, startOffset_s = 0): void => {
        audition.player.start(startOffset_s);
        setState({ isPlaying: true, uuid: audition.uuid, isBuffering: false });
    };

    const play: SamplePreviewPlayer["play"] = (result, startOffset_s = 0) => {
        const { uuid } = result.metadata;
        const thisGeneration = ++generation;

        if (current?.uuid === uuid) {
            start(current, startOffset_s);
            return;
        }

        if (current !== undefined) {
            discard(current);
            current = undefined;
        }
        setState({ isPlaying: true, uuid, isBuffering: true });

        void (async () => {
            const preview = await previews.ensure(result);
            if (thisGeneration !== generation) {
                return;
            }
            if (preview instanceof Error) {
                setStopped();
                return;
            }

            const added = await core.actions.addFiles([
                createSpliceSampleInputFile(result, preview.file, [kAuditionTag]),
            ]);
            if (added instanceof Error || added.length === 0) {
                if (thisGeneration === generation) {
                    setStopped();
                }
                return;
            }
            const resourceId = added[0].id;
            if (thisGeneration !== generation) {
                void core.actions.deleteResources([resourceId]);
                return;
            }

            const player = core.actions.createPreviewPlayer(resourceId);
            if (player === undefined) {
                void core.actions.deleteResources([resourceId]);
                setStopped();
                return;
            }
            current = {
                uuid,
                resourceId,
                player,
                removeListener: listenForEnd(player, uuid),
                isOwned: true,
            };
            start(current, startOffset_s);
        })();
    };

    const playResource: SamplePreviewPlayer["playResource"] = (id, options = {}) => {
        const uuid = options.playbackId ?? getResourcePlaybackId(id);
        generation += 1;
        if (current?.uuid === uuid) {
            start(current, options.startOffset_s);
            return;
        }
        if (current !== undefined) {
            discard(current);
            current = undefined;
        }
        const player = core.actions.createPreviewPlayer(id);
        if (player === undefined) {
            setStopped();
            return;
        }
        current = {
            uuid,
            resourceId: id,
            player,
            removeListener: listenForEnd(player, uuid),
            isOwned: options.isOwned ?? false,
        };
        start(current, options.startOffset_s);
    };

    const stop: SamplePreviewPlayer["stop"] = () => {
        generation += 1;
        current?.player.stop();
        setStopped();
    };

    const release: SamplePreviewPlayer["release"] = () => {
        stop();
        if (current !== undefined) {
            discard(current);
            current = undefined;
        }
    };

    const getResumeState: SamplePreviewPlayer["getResumeState"] = () => {
        if (current === undefined || !state.isPlaying || state.uuid !== current.uuid) {
            return undefined;
        }
        // Only an audition is worth carrying: a dropped file plays as a seed,
        // and the seed already crosses pages on its own.
        if (!current.isOwned) {
            return undefined;
        }
        return {
            uuid: current.uuid,
            position_s: current.player.getElapsed_s(),
        };
    };

    return {
        onState: stateSignal.listen,
        getState: () => state,
        getProgress_0to1: () => {
            if (!state.isPlaying || current?.uuid !== state.uuid) {
                return 0;
            }
            const duration_s = current.player.getDuration_s();
            return duration_s > 0 ? Math.min(current.player.getElapsed_s() / duration_s, 1) : 0;
        },
        play,
        playResource,
        stop,
        release,
        getResumeState,
    };
};
